import type { CompositePrimaryKey } from '#layers/data-model/public';

export interface SimpleEntitlement {
	resource: string;
	action: string;
}

export interface ResourceEntitlement<R extends CompositePrimaryKey | string = CompositePrimaryKey | string> {
	resource: string;
	action: string;
	resourceId: R | undefined;
}

export type EntitlementWithResource<R extends CompositePrimaryKey | string = CompositePrimaryKey | string> = (resourceId?: R) => ResourceEntitlement<R>;

export function defineEntitlement(resource: string, action: string): SimpleEntitlement {
	if (!resource || !action) {
		throw new Error('[defineEntitlement]: resource and action are required');
	}

	return { resource, action };
}

/**
 * Build a scoped `can*` factory. `R` defaults to `CompositePrimaryKey | string` (a generic
 * item key); pass a branded id (e.g. `defineResourceEntitlement<GlobalDataSourceId>(...)`) so
 * that `can*` rejects a foreign id without a cast. `authorize` normalizes the id to the wire
 * string at the gate.
 */
export function defineResourceEntitlement<R extends CompositePrimaryKey | string = CompositePrimaryKey | string>(
	resource: string,
	action: string,
): EntitlementWithResource<R> {
	if (!resource || !action) {
		throw new Error('[defineResourceEntitlement]: resource and action are required');
	}

	return (resourceId?: R) => ({ resource, action, resourceId });
}
